import { useState } from "react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@nosible/shared/components/ui/alert-dialog";
import { CreateApiKeyForm } from "./CreateApiKeyForm";
import { trpc } from "@/account/lib/trpc";
import { Copy, Key, Trash2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@nosible/shared/components/ui/tooltip";

interface SubApiKeysProps {
  subscriptionId: string;
}

function formatLastUsed(value: string | Date | null | undefined): string {
  if (!value) return "NEVER";
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60000));
  if (elapsedMinutes < 60) return `${Math.max(1, elapsedMinutes)} MIN AGO`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours} HR AGO`;
  if (elapsedHours < 48) return "YESTERDAY";
  return `${Math.floor(elapsedHours / 24)} DAYS AGO`;
}

export function SubApiKeys({ subscriptionId }: SubApiKeysProps) {
  const {
    data: apiKeys,
    isLoading: apiKeysLoading,
    refetch: refetchApiKeys,
  } = trpc.subscription.apiKey.listApiKeys.useQuery(
    { subscriptionId },
    { enabled: !!subscriptionId }
  );

  const deactivateApiKeyMutation =
    trpc.subscription.apiKey.deactivateApiKey.useMutation({
      onSuccess: () => {
        refetchApiKeys();
      },
    });

  const handleDeactivateKey = (keyId: string) => {
    deactivateApiKeyMutation.mutate({ keyId, subscriptionId });
  };

  const handleCopyKey = async (value: string | null | undefined) => {
    if (!value) return;

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return;
    }

    const fallback = document.createElement("textarea");
    fallback.value = value;
    fallback.setAttribute("readonly", "");
    fallback.style.position = "fixed";
    fallback.style.opacity = "0";
    document.body.appendChild(fallback);
    fallback.select();
    document.execCommand("copy");
    fallback.remove();
  };

  const sortedApiKeys = [...(apiKeys ?? [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  return (
    <section className="account-reference-section account-api-keys-card" aria-labelledby="api-keys-heading">
      <div className="subscription-section-heading" id="api-keys-heading">
        <span>02 API KEYS</span>
        <CreateApiKeyForm
          subscriptionId={subscriptionId}
          onSuccess={() => refetchApiKeys()}
        />
      </div>
      <Card>
        <CardContent className="p-0">
          {apiKeysLoading ? (
            <div className="account-loading-copy">Loading API keys...</div>
          ) : sortedApiKeys.length === 0 ? (
            <div className="account-empty-panel">
              <Key aria-hidden="true" />
              <p>No API keys yet. Create one to get started.</p>
            </div>
          ) : (
            <div className="account-api-keys-table">
              <div className="account-api-keys-table__head">
                <span>NAME</span><span>KEY</span><span>CREATED</span><span>LAST USED</span><span />
              </div>
              {sortedApiKeys.map((apiKey: any) => (
                <div className="account-api-keys-table__row" key={apiKey.id}>
                  <strong>{apiKey.name}</strong>
                  <span data-label="KEY">{apiKey.key ? apiKey.key : "nos_live_••••••••••••"}</span>
                  <span data-label="CREATED">{apiKey.createdAt ? new Date(apiKey.createdAt).toLocaleDateString("en-CA") : "—"}</span>
                  <span data-label="LAST USED">{formatLastUsed(apiKey.lastUsed)}</span>
                  <div className="account-api-keys-table__actions">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Copy ${apiKey.name}`}
                      onClick={() => handleCopyKey(apiKey.key ?? "nos_live_••••••••••••")}
                    >
                      <Copy />
                    </Button>
                    <AlertDialog>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={deactivateApiKeyMutation.isPending}
                              aria-label={`Deactivate ${apiKey.name}`}
                            >
                              <Trash2 />
                            </Button>
                          </AlertDialogTrigger>
                        </TooltipTrigger>
                        <TooltipContent sideOffset={8}>Deactivate API key</TooltipContent>
                      </Tooltip>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Deactivate API Key</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to deactivate "{apiKey.name}"? Any applications using this key will stop working.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeactivateKey(apiKey.id)} className="account-destructive-action">
                            Deactivate
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
